/**
 * AddChildDialog component
 * Accessible modal form for creating a new child profile from the parent dashboard.
 *
 * Implements US-004 (T3): Add-a-child modal dialog.
 *
 * Overlay pattern reused from DemoLimitPopup.tsx (role=dialog, aria-modal, Escape-to-close,
 * focus management on open). No shadcn Dialog primitive used — project has none (ui/).
 *
 * TO-LL-044 enforcement: submit payload uses exact camelCase keys displayName/password/username
 * matching children.py lines 126-128 (enforced via CreateChildPayload type).
 * TO-LL-036 enforcement: errors from the mutation are surfaced inline — dialog stays open on
 * 400/409/403/500; never swallowed or hidden behind a success-close.
 *
 * Reused components:
 * - validatePassword + PasswordRequirements (src/lib/passwordValidator.ts + PasswordRequirements.tsx)
 * - Input, Label, Button (src/components/ui/)
 * - DemoLimitPopup overlay shell pattern
 * - useChildMutations.create (T2)
 *
 * AC-1: form submits POST /children on valid input; dialog closes and grid refetches on 201.
 * AC-2: password policy enforced client-side via validatePassword; PasswordRequirements shows
 *       which rule is unmet; submission blocked until policy is met.
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { PasswordRequirements } from '@/components/PasswordRequirements';
import { validatePassword } from '@/lib/passwordValidator';
import { useChildMutations } from '@/hooks/useChildMutations';
import {
  GRADE_LEVEL_OPTIONS,
  P6_BAND_OPTIONS,
  DEFAULT_DIFFICULTY_TIER,
} from '@/constants/difficulty';
import type { DifficultyTier } from '@/types/coins';

// ─── Props ────────────────────────────────────────────────────────────────────

export interface AddChildDialogProps {
  /** Whether the dialog is visible */
  isOpen: boolean;
  /** Callback to close the dialog */
  onClose: () => void;
}

// ─── AddChildDialog (main export) ────────────────────────────────────────────

/**
 * AddChildDialog — overlay modal for creating a child profile.
 *
 * Fields:
 *  - Display name (required, max 50 chars, auto-focused on open)
 *  - Password (required, Cognito policy enforced client-side via validatePassword)
 *  - Username (required — parent chooses the child's login; no longer auto-generated)
 *
 * States:
 *  - Idle: form editable
 *  - Submitting (create.isPending): buttons disabled, "Adding..." label
 *  - Error (create.error or clientError): inline red alert, dialog stays open (TO-LL-036)
 *  - Success (create.isSuccess): dialog closes; dashboard refetches via ['parent','dashboard']
 */
export function AddChildDialog({ isOpen, onClose }: AddChildDialogProps) {
  const { create } = useChildMutations();

  // ─── Form state ───────────────────────────────────────────────────────────
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [username, setUsername] = useState('');
  const [difficultyTier, setDifficultyTier] = useState<DifficultyTier>(DEFAULT_DIFFICULTY_TIER);
  const [clientError, setClientError] = useState<string | null>(null);

  // Focus target: display name input (auto-focused on dialog open)
  const displayNameRef = useRef<HTMLInputElement>(null);

  // ─── Reset state when dialog opens ───────────────────────────────────────
  useEffect(() => {
    if (isOpen) {
      setDisplayName('');
      setPassword('');
      setUsername('');
      setDifficultyTier(DEFAULT_DIFFICULTY_TIER);
      setClientError(null);
      create.reset();
      // Focus the first field after DOM is ready
      const timer = setTimeout(() => {
        displayNameRef.current?.focus();
      }, 50);
      return () => clearTimeout(timer);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // ─── Close on success (AC-1) ──────────────────────────────────────────────
  useEffect(() => {
    if (create.isSuccess) {
      onClose();
    }
  }, [create.isSuccess, onClose]);

  // ─── Keyboard handler: Escape closes, provides accessible dismiss ─────────
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape' && !create.isPending) {
        onClose();
      }
    },
    [onClose, create.isPending]
  );

  // ─── Form submission ──────────────────────────────────────────────────────
  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      setClientError(null);

      // Client-side validation — blocks API call on invalid input (AC-1, AC-2)
      const trimmedName = displayName.trim();
      if (!trimmedName) {
        setClientError('Please enter a display name.');
        return;
      }
      if (trimmedName.length > 50) {
        setClientError('Display name must be 50 characters or fewer.');
        return;
      }

      const { isValid } = validatePassword(password);
      if (!isValid) {
        // TO-LL-036: surface the failure clearly; PasswordRequirements shows which rule failed
        setClientError('Password does not meet the requirements below.');
        return;
      }

      // Username is now required (parent chooses the child's login) — block on empty.
      const trimmedUsername = username.trim();
      if (!trimmedUsername) {
        setClientError('Please enter a username.');
        return;
      }

      // TO-LL-044: payload keys are exact camelCase matching children.py lines 126-128
      // TO-LL-036: errors propagate from createChild; no try/catch here
      create.mutate({
        displayName: trimmedName,
        password,
        username: trimmedUsername,
        difficultyTier,
      });
    },
    [displayName, password, username, difficultyTier, create]
  );

  // Don't render when not open
  if (!isOpen) return null;

  // Determine the error message to display (server error takes precedence over stale client error)
  const errorMessage = create.error?.message ?? clientError;

  return (
    /* Overlay — same pattern as DemoLimitPopup (role=dialog, aria-modal, fixed inset-0 z-50) */
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30"
      role="dialog"
      aria-modal="true"
      aria-labelledby="add-child-dialog-title"
      onKeyDown={handleKeyDown}
      onClick={(e) => {
        // Close on backdrop click (only if not submitting)
        if (e.target === e.currentTarget && !create.isPending) {
          onClose();
        }
      }}
    >
      {/* Dialog card */}
      <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 w-full max-w-sm mx-4">
        {/* Title */}
        <h2
          id="add-child-dialog-title"
          className="text-lg md:text-xl font-bold text-gray-900 mb-1"
        >
          Add a child
        </h2>
        <p className="text-sm text-gray-500 mb-5">
          Create a login for your child to access ThinkOtter.
        </p>

        {/* Form */}
        <form onSubmit={handleSubmit} noValidate className="space-y-4">
          {/* Display name field */}
          <div className="space-y-1">
            <Label htmlFor="add-child-display-name" className="text-sm font-medium text-gray-700">
              Display name <span aria-hidden="true" className="text-red-500">*</span>
            </Label>
            <Input
              id="add-child-display-name"
              ref={displayNameRef}
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="e.g. Ben"
              maxLength={50}
              required
              disabled={create.isPending}
              autoComplete="off"
              className="h-11"
            />
          </div>

          {/* Password field + live requirements (AC-2) */}
          <div className="space-y-1">
            <Label htmlFor="add-child-password" className="text-sm font-medium text-gray-700">
              Password <span aria-hidden="true" className="text-red-500">*</span>
            </Label>
            <PasswordInput
              id="add-child-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Create a password"
              required
              disabled={create.isPending}
              autoComplete="new-password"
              className="h-11"
            />
            {/* Live policy checklist — shown when user starts typing */}
            {password.length > 0 && (
              <div className="pt-1">
                <PasswordRequirements password={password} />
              </div>
            )}
          </div>

          {/* Username field (required) */}
          <div className="space-y-1">
            <Label htmlFor="add-child-username" className="text-sm font-medium text-gray-700">
              Username <span aria-hidden="true" className="text-red-500">*</span> <span className="text-xs font-normal text-gray-500">(case-sensitive)</span>
            </Label>
            <Input
              id="add-child-username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Choose a username"
              required
              disabled={create.isPending}
              autoComplete="off"
              className="h-11"
            />
            <p className="text-xs text-gray-400">
              Your child uses this to log in.
            </p>
          </div>

          {/* Learning level — mirrors the child-card dropdown (shared options).
              Two grade-level options + a 'Primary 6' optgroup with the three
              bands. Defaults to DEFAULT_DIFFICULTY_TIER ('hard'). */}
          <div className="space-y-1">
            <Label htmlFor="add-child-difficulty" className="text-sm font-medium text-gray-700">
              Learning level
            </Label>
            <select
              id="add-child-difficulty"
              value={difficultyTier}
              onChange={(e) => setDifficultyTier(e.target.value as DifficultyTier)}
              disabled={create.isPending}
              className="h-11 w-full rounded-md border border-input bg-white px-3 text-sm text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-tutor-400"
            >
              {GRADE_LEVEL_OPTIONS.map(({ label, value }) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
              <optgroup label="Primary 6">
                {P6_BAND_OPTIONS.map(({ label, value }) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </optgroup>
            </select>
            <p className="text-xs text-gray-400">
              You can change this anytime from your child's card.
            </p>
          </div>

          {/* Inline error message — surfaced, never swallowed (TO-LL-036) */}
          {errorMessage && (
            <div
              role="alert"
              className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700"
            >
              {errorMessage}
            </div>
          )}

          {/* Action buttons */}
          <div className="flex gap-3 pt-2">
            <Button
              type="submit"
              disabled={create.isPending}
              className="flex-1 h-11"
            >
              {create.isPending ? 'Adding…' : 'Add child'}
            </Button>
            <Button
              type="button"
              variant="outline"
              disabled={create.isPending}
              onClick={onClose}
              className="flex-1 h-11"
            >
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
